import { Link } from "react-router-dom";
import { news } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import { specials } from "@/mocks/specials";
import { categories } from "@/mocks/categories";
import NewsCard from "@/components/base/NewsCard";
import VideoCard from "@/components/base/VideoCard";
import SectionTitle from "@/components/base/SectionTitle";
import HeroCarousel, { type HeroSlide } from "./components/HeroCarousel";
import { formatViews } from "@/lib/content";

const slides: HeroSlide[] = [
  {
    id: "h1",
    title: news[0].title,
    summary: news[0].summary,
    image:
      "https://readdy.ai/api/search-image?query=epic%20cinematic%20sci-fi%20space%20station%20with%20deep%20red%20nebula%20and%20dark%20dramatic%20atmosphere%2C%20moody%20high%20contrast%20lighting%2C%20wide%20banner%20composition%2C%20no%20text&width=1920&height=1080&seq=hero-sci-fi-01&orientation=landscape",
    link: `/news/${news[0].id}`,
    tag: "头条聚焦",
  },
  {
    id: "h2",
    title: videos[1].title,
    summary: videos[1].description,
    image:
      "https://readdy.ai/api/search-image?query=warm%20night%20city%20documentary%20scene%20with%20glowing%20orange%20streetlights%20and%20soft%20bokeh%2C%20emotional%20cinematic%20banner%2C%20atmospheric%20depth%2C%20no%20text&width=1920&height=1080&seq=hero-night-02&orientation=landscape",
    link: `/video/${videos[1].id}`,
    tag: "精选视频",
  },
  {
    id: "h3",
    title: news[8].title,
    summary: news[8].summary,
    image:
      "https://readdy.ai/api/search-image?query=rock%20band%20silhouette%20on%20stage%20with%20deep%20red%20backlight%20and%20haze%2C%20energetic%20concert%20banner%2C%20dramatic%20contrast%2C%20no%20text&width=1920&height=1080&seq=hero-band-03&orientation=landscape",
    link: `/news/${news[8].id}`,
    tag: "热搜现场",
  },
  {
    id: "h4",
    title: videos[0].title,
    summary: videos[0].description,
    image:
      "https://readdy.ai/api/search-image?query=futuristic%20sci-fi%20landscape%20with%20red%20and%20dark%20tones%20and%20dramatic%20light%20rays%2C%20cinematic%20trailer%20banner%20composition%2C%20no%20text&width=1920&height=1080&seq=hero-trailer-04&orientation=landscape",
    link: `/video/${videos[0].id}`,
    tag: "视频推荐",
  },
];

export default function Home() {
  const headline = news.slice(0, 6);
  const sideHeadlines = news.slice(0, 5);
  const hotVideos = videos.filter((v) => v.hot).slice(0, 3).concat(videos.slice(2, 5));
  const featureVideos = hotVideos.slice(0, 6);

  return (
    <>
      <HeroCarousel slides={slides} />

      {/* 新闻资讯快报 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionTitle
          eyebrow="NEWS FLASH"
          title="新闻资讯快报"
          desc="最新娱乐热点第一时间送达，快速掌握影视、综艺、音乐与圈内动态。"
          action={
            <Link
              to="/category/hotspot"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              更多资讯 <i className="ri-arrow-right-s-line"></i>
            </Link>
          }
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {headline.slice(0, 4).map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex items-center gap-2">
              <i className="ri-flashlight-fill text-lg text-accent-400"></i>
              <h3 className="font-heading text-lg font-bold text-foreground-950">即时快讯</h3>
            </div>
            <ul className="flex flex-col divide-y divide-background-200">
              {sideHeadlines.map((item, i) => (
                <li key={item.id}>
                  <Link to={`/news/${item.id}`} className="group flex gap-3 py-3">
                    <span
                      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm font-heading text-xs font-bold ${
                        i < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="line-clamp-2 text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-1 rounded-md bg-background-200/60 p-3 text-xs text-foreground-600">
              <i className="ri-information-line mr-1 text-accent-400"></i>
              快讯内容为前端演示数据，持续更新中。
            </div>
          </aside>
        </div>
      </section>

      {/* 视频推荐 */}
      <section className="bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="VIDEO PICKS"
            title="视频推荐"
            desc="影视剧集、热门综艺、短视频精选，点击封面即可进入视频详情。"
            action={
              <Link
                to="/videos"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                全部视频 <i className="ri-arrow-right-s-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featureVideos.map((v) => (
              <VideoCard key={v.id} item={v} />
            ))}
          </div>
        </div>
      </section>

      {/* 分类入口 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionTitle eyebrow="CHANNELS" title="浏览频道" desc="按兴趣选择你关注的内容方向。" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-400/60"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-300 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className="ri-bookmark-3-line text-xl"></i>
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-950">{c.name}</h3>
              <p className="line-clamp-2 text-xs text-foreground-600">{c.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 热门专题 */}
      <section className="bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="SPECIAL TOPICS"
            title="热门专题"
            desc="编辑精选聚合专题，把同一话题下的资讯与视频一次看个够。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {specials.slice(0, 3).map((s, i) => (
              <Link
                key={s.id}
                to={`/special/${s.id}`}
                className={`group relative flex flex-col overflow-hidden rounded-lg border border-background-200 transition-colors hover:border-accent-400/60 ${
                  i === 0 ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
              >
                <div className={`relative w-full overflow-hidden ${i === 0 ? "h-56" : "h-48"}`}>
                  <img
                    src={s.cover}
                    alt={`${s.title} - 专题封面`}
                    title={`${s.title} - 91.con热点专题`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 w-full p-5">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {s.tags.slice(0, 2).map((t) => (
                      <span
                        key={t}
                        className="rounded-sm bg-accent-500/90 px-2 py-0.5 text-xs font-medium text-background-50"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950">{s.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-foreground-700">{s.intro}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {specials.slice(3, 6).map((s) => (
              <Link
                key={s.id}
                to={`/special/${s.id}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-400/60"
              >
                <div className="h-14 w-20 flex-shrink-0 overflow-hidden rounded-md">
                  <img
                    src={s.cover}
                    alt={`${s.title} - 专题封面`}
                    title={`${s.title} - 91.con热点专题`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h4 className="line-clamp-1 font-heading text-sm font-semibold text-foreground-950">
                    {s.title}
                  </h4>
                  <span className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-links-line"></i>
                    {s.items.length} 篇内容
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 站点简介 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="font-label text-xs font-semibold uppercase tracking-widest text-primary-400">
              ABOUT 91.CON
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              不止是资讯，更是一站式娱乐内容入口
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              91.con 聚合影视、综艺、音乐、八卦与热点新闻，用清晰的内容结构与丰富的视频资源，帮助你在最短时间内找到想看的娱乐内容。无论是追热点、找剧集，还是单纯想放松一下，这里都能满足你。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                了解我们 <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-search-line"></i>搜索内容
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: `${news.length * 128}`, label: "累计资讯", icon: "ri-article-line" },
              { num: `${videos.length * 96}`, label: "视频资源", icon: "ri-video-line" },
              { num: `${specials.length * 12}`, label: "精选专题", icon: "ri-fire-line" },
              { num: `${formatViews(1860000)}`, label: "月活跃用户", icon: "ri-user-heart-line" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-background-200/60 p-5">
                <i className={`${s.icon} text-2xl text-accent-400`}></i>
                <div className="mt-3 font-heading text-2xl font-bold text-foreground-950">{s.num}</div>
                <div className="mt-1 text-xs text-foreground-600">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}